/**
 * The patient file, as Contracts and the front desk register it.
 *
 * The requirement's fields, in the order a desk asks for them: who (four
 * names, date of birth), how to find them again (national ID, two direct
 * phones, insurance code — at least one), what they live with (chronic
 * diseases, current medications with concentration, frequency and duration,
 * current complaints, old files), and how they pay (insured or cash, the
 * provider, the employer, the expiry, a photo of the card).
 *
 * The server is the authority on every rule here (lib/patientFile.js); the
 * form only says the one rule people trip over most — "one identifier at
 * least" — before the request, so the desk is not surprised by it after.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Checkbox, Field, Icon, Input, Select, Textarea, DateInput } from '../../components/common';
import { contractPartiesApi, patientsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { readFileAsDataUrl } from './labels';
import { hasPatientIdentifier, patientFormFrom } from '../../domain/patientForm';

export function PatientFileForm({ patient = null, bookingId = null, onSaved, readOnly = false }) {
  const { success, error: toastError } = useNotifications();
  const [form, setForm] = useState(() => patientFormFrom(patient));
  const [card, setCard] = useState(undefined); // undefined = unchanged
  const [chronicDraft, setChronicDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);
  const [parties, setParties] = useState([]);

  // جهة التعاقد is a pick-list (QA report §2.1). A desk that cannot read it
  // simply sees no options — the rest of the form still works.
  useEffect(() => {
    let live = true;
    contractPartiesApi
      .list()
      .then((rows) => live && setParties(Array.isArray(rows) ? rows : []))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Another file picked while this form is open: show that file, not the last.
  const patientId = patient?.id ?? null;
  useEffect(() => {
    setForm(patientFormFrom(patient));
    setCard(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const hasIdentifier = hasPatientIdentifier(form);
  const nameParts = useMemo(() => String(form.fullName || '').trim().split(/\s+/).filter(Boolean).length, [form.fullName]);

  const addChronic = () => {
    const value = chronicDraft.trim();
    if (!value) return;
    setForm((f) => ({ ...f, chronicDiseases: Array.from(new Set([...f.chronicDiseases, value])) }));
    setChronicDraft('');
  };

  const setMed = (index, key, value) =>
    setForm((f) => ({
      ...f,
      currentMedications: f.currentMedications.map((m, i) => (i === index ? { ...m, [key]: value } : m)),
    }));

  const pickCard = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toastError('صورة الكارت أكبر من 3 ميجابايت');
      return;
    }
    try {
      setCard(await readFileAsDataUrl(file));
    } catch (err) {
      toastError(err.message);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        contractPartyId: form.contractPartyId ? Number(form.contractPartyId) : null,
        currentMedications: form.currentMedications.filter((m) => String(m?.name || '').trim()),
        ...(card !== undefined ? { insuranceCardImage: card } : {}),
        ...(bookingId && !patient ? { bookingId } : {}),
      };
      const saved = patient ? await patientsApi.update(patient.id, payload) : await patientsApi.create(payload);
      success(patient ? 'تم تحديث ملف المريض' : 'تم تسجيل ملف المريض');
      onSaved?.(saved);
    } catch (err) {
      toastError(err.body?.existing ? `${err.message}: ${err.body.existing.fullName}` : err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Who */}
      <section>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
          <Icon name="user" size={16} className="text-cyan-400" /> بيانات المريض
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="الاسم رباعياً"
            required
            className="sm:col-span-2"
            hint={nameParts > 0 && nameParts < 4 ? `اكتب ${4 - nameParts} ${4 - nameParts === 1 ? 'اسماً' : 'أسماء'} أخرى` : 'كما في البطاقة'}
          >
            <Input value={form.fullName} onChange={set('fullName')} disabled={readOnly} placeholder="الاسم الأول الأب الجد العائلة" />
          </Field>
          <Field label="تاريخ الميلاد">
            <DateInput value={form.dateOfBirth || ''} onChange={set('dateOfBirth')} disabled={readOnly} />
          </Field>
          <Field label="النوع">
            <Select value={form.gender || ''} onChange={set('gender')} disabled={readOnly}>
              <option value="">—</option>
              <option value="male">ذكر</option>
              <option value="female">أنثى</option>
            </Select>
          </Field>
        </div>
      </section>

      {/* How to find them again */}
      <section>
        <h3 className="mb-1 flex items-center gap-2 text-sm font-bold text-white">
          <Icon name="key-round" size={16} className="text-amber-400" /> المعرّفات
          {!hasIdentifier && (
            <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-200">مطلوب واحد على الأقل</Badge>
          )}
        </h3>
        <p className="mb-3 text-xs text-slate-400">
          الرقم القومي، أو رقم هاتف المريض المباشر، أو كود التأمين — واحد منها على الأقل.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="الرقم القومي" hint="14 رقماً">
            <Input dir="ltr" inputMode="numeric" value={form.nationalId || ''} onChange={set('nationalId')} disabled={readOnly} />
          </Field>
          <Field label="كود التأمين">
            <Input dir="ltr" value={form.insuranceCode || ''} onChange={set('insuranceCode')} disabled={readOnly} />
          </Field>
          <Field label="هاتف المريض المباشر" hint="رقم المريض نفسه — ليس رقم قريب">
            <Input dir="ltr" inputMode="tel" value={form.phonePrimary || ''} onChange={set('phonePrimary')} disabled={readOnly} />
          </Field>
          <Field label="هاتف مباشر ثانٍ" hint="اختياري — رقم المريض أيضاً">
            <Input dir="ltr" inputMode="tel" value={form.phoneSecondary || ''} onChange={set('phoneSecondary')} disabled={readOnly} />
          </Field>
        </div>
      </section>

      {/* The clinical picture */}
      <section>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
          <Icon name="heart-pulse" size={16} className="text-rose-400" /> الحالة الصحية
        </h3>

        <Field label="الأمراض المزمنة" hint="اختياري — اكتب المرض واضغط إضافة">
          <div className="flex flex-wrap items-center gap-2">
            {form.chronicDiseases.map((d) => (
              <Badge key={d} className="border-rose-500/30 bg-rose-500/10 text-rose-200">
                {d}
                {!readOnly && (
                  <button
                    type="button"
                    className="mr-1 text-rose-300 hover:text-white"
                    aria-label={`حذف ${d}`}
                    onClick={() => setForm((f) => ({ ...f, chronicDiseases: f.chronicDiseases.filter((x) => x !== d) }))}
                  >
                    ×
                  </button>
                )}
              </Badge>
            ))}
            {!readOnly && (
              <div className="flex min-w-[14rem] flex-1 gap-2">
                <Input
                  value={chronicDraft}
                  onChange={(e) => setChronicDraft(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChronic())}
                  placeholder="مثال: سكري، ضغط"
                />
                <Button variant="subtle" icon="plus" onClick={addChronic}>
                  إضافة
                </Button>
              </div>
            )}
          </div>
        </Field>

        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="field-label !mb-0">الأدوية الحالية</span>
            {!readOnly && (
              <Button
                variant="subtle"
                icon="plus"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    currentMedications: [...f.currentMedications, { name: '', concentration: '', frequency: '', duration: '' }],
                  }))
                }
              >
                دواء
              </Button>
            )}
          </div>
          {form.currentMedications.length === 0 ? (
            <p className="text-xs text-slate-500">لا توجد أدوية مسجلة.</p>
          ) : (
            <div className="space-y-2">
              {form.currentMedications.map((m, i) => (
                <div key={i} className="grid grid-cols-2 gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-2 sm:grid-cols-[1.4fr_1fr_1fr_1fr_auto]">
                  <Input placeholder="اسم الدواء" value={m.name || ''} onChange={(e) => setMed(i, 'name', e.target.value)} disabled={readOnly} />
                  <Input placeholder="التركيز" value={m.concentration || ''} onChange={(e) => setMed(i, 'concentration', e.target.value)} disabled={readOnly} />
                  <Input placeholder="التكرار" value={m.frequency || ''} onChange={(e) => setMed(i, 'frequency', e.target.value)} disabled={readOnly} />
                  <Input placeholder="المدة" value={m.duration || ''} onChange={(e) => setMed(i, 'duration', e.target.value)} disabled={readOnly} />
                  {!readOnly && (
                    <button
                      type="button"
                      aria-label="حذف الدواء"
                      className="rounded-lg px-2 text-slate-400 hover:bg-red-500/10 hover:text-red-300"
                      onClick={() => setForm((f) => ({ ...f, currentMedications: f.currentMedications.filter((_, j) => j !== i) }))}
                    >
                      <Icon name="trash-2" size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="الشكوى الحالية" hint="اختياري">
            <Textarea rows={3} value={form.complaints || ''} onChange={set('complaints')} disabled={readOnly} />
          </Field>
          <Field label="ملفات وتاريخ مرضي سابق" hint="ملخص — والمستندات في المرفقات">
            <Textarea rows={3} value={form.medicalHistory || ''} onChange={set('medicalHistory')} disabled={readOnly} />
          </Field>
        </div>
      </section>

      {/* How they pay */}
      <section>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
          <Icon name="shield-plus" size={16} className="text-purple-400" /> جهة التعاقد والدفع
        </h3>
        <Field label="جهة التعاقد" className="mb-3" hint={parties.length ? 'التأمين أو الشركة أو النقابة التي يأتي المريض عن طريقها' : 'لا توجد جهات بعد — تضيفها التعاقدات والموافقات'}>
          <Select
            value={form.contractPartyId}
            disabled={readOnly}
            onChange={(e) => {
              const party = parties.find((x) => String(x.id) === e.target.value);
              setForm((f) => ({
                ...f,
                contractPartyId: e.target.value,
                // An insurer implies an insured patient; the desk can still untick it.
                patientType: party && party.kind === 'insurance' ? 'insured' : f.patientType,
                insuranceProvider: party ? party.name : f.insuranceProvider,
              }));
            }}
          >
            <option value="">بدون — مريض نقدي</option>
            {parties.map((party) => (
              <option key={party.id} value={party.id}>
                {party.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="mb-3 flex flex-wrap gap-3">
          <Checkbox
            label="المريض مؤمَّن عليه"
            checked={form.patientType === 'insured'}
            onChange={(e) => setForm((f) => ({ ...f, patientType: e.target.checked ? 'insured' : 'cash' }))}
            disabled={readOnly}
          />
          <span className="text-xs text-slate-500">
            {form.patientType === 'insured' ? 'التغطية تُضبط من تبويب التغطية' : 'مريض نقدي — يدفع كامل التكلفة'}
          </span>
        </div>
        {form.patientType === 'insured' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="شركة التأمين">
              <Input value={form.insuranceProvider || ''} onChange={set('insuranceProvider')} disabled={readOnly} />
            </Field>
            <Field label="جهة العمل">
              <Input value={form.employerName || ''} onChange={set('employerName')} disabled={readOnly} />
            </Field>
            <Field label="تاريخ انتهاء التأمين">
              <DateInput value={form.insuranceExpiry || ''} onChange={set('insuranceExpiry')} disabled={readOnly} />
            </Field>
            <Field label="صورة كارت التأمين" className="sm:col-span-3">
              <div className="flex flex-wrap items-center gap-3">
                {(card || (card === undefined && patient?.hasInsuranceCard)) && (
                  <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-200">
                    <Icon name="image" size={12} /> {card ? 'صورة جديدة جاهزة للحفظ' : 'الصورة محفوظة'}
                  </Badge>
                )}
                {!readOnly && (
                  <>
                    <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={pickCard} />
                    <Button variant="subtle" icon="image-plus" onClick={() => fileInput.current?.click()}>
                      رفع صورة الكارت
                    </Button>
                    {(card || patient?.hasInsuranceCard) && (
                      <Button variant="subtle" icon="trash-2" onClick={() => setCard(null)}>
                        حذف الصورة
                      </Button>
                    )}
                  </>
                )}
              </div>
            </Field>
          </div>
        )}
      </section>

      {!readOnly && (
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-white/5 pt-4">
          {!hasIdentifier && <p className="text-xs text-amber-300">أضف معرّفاً واحداً على الأقل قبل الحفظ.</p>}
          <Button icon="save" loading={saving} disabled={!String(form.fullName || '').trim() || !hasIdentifier} onClick={save}>
            {patient ? 'حفظ التعديلات' : 'تسجيل الملف'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default PatientFileForm;
